/**
 * PocPacingEditorModal — settings editor opened from the ⚙ icon on the
 * POC Pacing card.
 *
 * Per Jamie: per-metric M1/M2/M3 targets are unique per client, with no
 * standard defaults. POC length is also per-client (Relationship /
 * Quarter / Month / 3-month POC / Custom days) and overrides the
 * derivation from clients.package_signed.
 *
 * Writes:
 *   - clients.onboarding_date    (POC start date)
 *   - clients.poc_length_unit    (display label)
 *   - clients.poc_length_days    (resolved day count, NULL for relationship)
 *   - client_goals               (one row per metric with M1/M2/M3 targets)
 *
 * On save: invalidates the usePocPacingData query so the dashboard
 * re-renders with fresh values without a hard reload.
 *
 * Matches the existing PageSettingsModal layout pattern (inline divs +
 * backdrop) for visual consistency with the rest of the new UX.
 */
import React, { useState, useEffect, useMemo } from 'react'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { X, Settings } from 'lucide-react'
import { PACING_METRIC_LABELS, type PacingMetric } from './usePocPacingData'

const METRIC_ORDER: PacingMetric[] = [
  'sends',
  'replies',
  'positives',
  'meetings',
  'closed',
  'pipeline',
  'acv',
]

function metricNameForDb(m: PacingMetric): string {
  return m === 'acv' ? 'won_acv' : m
}

type LengthUnit =
  | 'relationship'
  | 'monthly'        // ← NEW: one-month-at-a-time pacing for ongoing clients
  | 'quarter'
  | 'month'
  | '3-month-poc'
  | 'custom'

const LENGTH_UNIT_OPTIONS: Array<{ value: LengthUnit; label: string; days: number | null }> = [
  { value: 'monthly', label: 'Monthly target (recurring)', days: 30 },
  { value: '3-month-poc', label: '3 Month POC (90 days)', days: 90 },
  { value: 'quarter', label: 'Quarter (90 days)', days: 90 },
  { value: 'month', label: 'Single month (30 days)', days: 30 },
  { value: 'custom', label: 'Custom days…', days: null },
  { value: 'relationship', label: 'Relationship (open-ended) — hides pacing card', days: null },
]

function unitFromDays(days: number | null | undefined, savedUnit: string | null): LengthUnit {
  // If we have an explicit unit stored, prefer it (round-trips the user's choice).
  if (savedUnit === 'relationship') return 'relationship'
  if (savedUnit === 'monthly') return 'monthly'
  if (savedUnit === '3-month-poc') return '3-month-poc'
  if (savedUnit === 'quarter') return 'quarter'
  if (savedUnit === 'month') return 'month'
  if (savedUnit === 'custom') return 'custom'
  if (days == null) return 'relationship'
  if (days === 90) return '3-month-poc'
  if (days === 30) return 'month'
  return 'custom'
}

/** Format a Date as "May 2026". Used for the dynamic column headers so the
 *  user sees the actual calendar month a milestone lands on, not "M2". */
function formatMonth(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

/** First day of the month containing `d`, in local time, formatted YYYY-MM-DD. */
function firstOfMonthIso(d: Date): string {
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  return `${yyyy}-${mm}-01`
}

/** Last day of the month containing `d` (in local time), formatted YYYY-MM-DD. */
function lastOfMonthIso(d: Date): string {
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0)
  const yyyy = last.getFullYear()
  const mm = String(last.getMonth() + 1).padStart(2, '0')
  const dd = String(last.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

interface GoalRow {
  metric_name: string
  poc_start_date: string
  poc_end_date: string
  m1_target: number
  m2_target: number
  m3_target: number
  is_primary: boolean
}

type ForecastModel = 'linear' | 'trailing_7' | 'trailing_14'

const FORECAST_OPTIONS: Array<{ value: ForecastModel; label: string; hint: string }> = [
  { value: 'linear', label: 'Linear (current pace × remaining days)', hint: 'Default — extrapolates from cumulative actual / day-of-POC.' },
  { value: 'trailing_7', label: 'Trailing 7-day average', hint: 'Forecast = actual + (avg of last 7d daily rate × remaining days).' },
  { value: 'trailing_14', label: 'Trailing 14-day average', hint: 'Smoother than 7d; better for noisy days.' },
]

interface FormState {
  startDate: string
  lengthUnit: LengthUnit
  customDays: number | ''
  forecastModel: ForecastModel
  targets: Record<PacingMetric, { m1: string; m2: string; m3: string }>
}

const EMPTY_TARGETS: FormState['targets'] = METRIC_ORDER.reduce((acc, m) => {
  acc[m] = { m1: '', m2: '', m3: '' }
  return acc
}, {} as FormState['targets'])

export interface PocPacingEditorModalProps {
  open: boolean
  onClose: () => void
  clientId: string | null
  clientName: string | null
}

export function PocPacingEditorModal({
  open,
  onClose,
  clientId,
  clientName,
}: PocPacingEditorModalProps): JSX.Element | null {
  const qc = useQueryClient()

  const { data: existing, isLoading } = useQuery({
    queryKey: ['poc-editor-existing', clientId],
    enabled: open && !!clientId,
    queryFn: async () => {
      if (!clientId) return null
      const [clientRes, goalsRes] = await Promise.all([
        supabase
          .from('clients')
          .select('onboarding_date, poc_length_days, poc_length_unit, forecast_model')
          .eq('id', clientId as any)
          .maybeSingle(),
        supabase
          .from('client_goals')
          .select('metric_name, poc_start_date, poc_end_date, m1_target, m2_target, m3_target, is_primary')
          .eq('client_id', clientId as any),
      ])
      return {
        client: (clientRes.data ?? null) as any,
        goals: ((goalsRes.data ?? []) as any[]) as GoalRow[],
      }
    },
  })

  const [form, setForm] = useState<FormState>({
    startDate: '',
    lengthUnit: 'relationship',
    customDays: '',
    forecastModel: 'linear',
    targets: EMPTY_TARGETS,
  })

  // Hydrate form when existing data loads.
  useEffect(() => {
    if (!existing) return
    const c = existing.client
    const savedStartDate: string = c?.onboarding_date ?? existing.goals[0]?.poc_start_date ?? ''
    const days: number | null = c?.poc_length_days ?? null
    let unit = unitFromDays(days, c?.poc_length_unit ?? null)
    let startDate = savedStartDate

    // UX nicety: if the client is mid-relationship with no pacing config
    // ('relationship' mode AND zero saved goals → StoryStream today), the
    // modal would otherwise open in the empty-state "open-ended" view with
    // no targets grid. Default to Monthly mode for the current month so the
    // user immediately sees the May 2026 single-column targets layout.
    // The user can still switch back to 'relationship' if that's what they
    // want — this just means the modal opens in a productive default.
    if (unit === 'relationship' && existing.goals.length === 0) {
      unit = 'monthly'
      startDate = firstOfMonthIso(new Date())
    }

    const targets: FormState['targets'] = { ...EMPTY_TARGETS }
    for (const g of existing.goals) {
      const code = METRIC_ORDER.find((m) => metricNameForDb(m) === g.metric_name)
      if (!code) continue
      targets[code] = {
        m1: String(g.m1_target ?? ''),
        m2: String(g.m2_target ?? ''),
        m3: String(g.m3_target ?? ''),
      }
    }
    const fcRaw = String(c?.forecast_model ?? '').trim()
    const forecastModel: ForecastModel =
      fcRaw === 'trailing_7' || fcRaw === 'trailing_14' ? fcRaw : 'linear'
    setForm({
      startDate,
      lengthUnit: unit,
      customDays: typeof days === 'number' ? days : '',
      forecastModel,
      targets,
    })
  }, [existing])

  const resolvedDays = useMemo<number | null>(() => {
    if (form.lengthUnit === 'relationship') return null
    if (form.lengthUnit === 'custom') {
      const n = Number(form.customDays)
      return Number.isFinite(n) && n > 0 ? Math.round(n) : null
    }
    if (form.lengthUnit === 'monthly') {
      // Monthly mode = number of days in the picked month. Used so the
      // dashboard's day-of-POC math lines up with the calendar month.
      const start = form.startDate ? new Date(form.startDate) : new Date()
      const last = new Date(start.getFullYear(), start.getMonth() + 1, 0)
      return last.getDate()
    }
    return LENGTH_UNIT_OPTIONS.find((o) => o.value === form.lengthUnit)?.days ?? null
  }, [form.lengthUnit, form.customDays, form.startDate])

  // Compute the column headers (max 3) as actual month names derived from
  // start_date + length. Replaces the static "M1 / M2 / M3" labels which
  // forced users to mentally convert "month 2 of POC" into a calendar date.
  // Also drives how many target columns we render — monthly mode = 1 column.
  const targetColumns = useMemo<Array<{ slot: 'm1' | 'm2' | 'm3'; label: string }>>(() => {
    const start = form.startDate ? new Date(form.startDate) : null
    if (form.lengthUnit === 'monthly') {
      // Single column for the picked month.
      const monthLabel = start ? formatMonth(start) : formatMonth(new Date())
      return [{ slot: 'm3', label: monthLabel }]
    }
    if (!start || !resolvedDays) {
      return [
        { slot: 'm1', label: 'M1' },
        { slot: 'm2', label: 'M2' },
        { slot: 'm3', label: 'M3' },
      ]
    }
    // Multi-column POC: derive milestone dates as start + 1/3, 2/3, 3/3 of length.
    const milestoneDate = (frac: number) =>
      new Date(start.getTime() + Math.round(resolvedDays * frac) * 86_400_000)
    return [
      { slot: 'm1', label: formatMonth(milestoneDate(1 / 3)) },
      { slot: 'm2', label: formatMonth(milestoneDate(2 / 3)) },
      { slot: 'm3', label: formatMonth(milestoneDate(1)) },
    ]
  }, [form.startDate, form.lengthUnit, resolvedDays])

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!clientId) throw new Error('No client selected')

      // 1. Update clients row (POC start + length config).
      const clientUpdate = {
        onboarding_date: form.startDate || null,
        poc_length_days: resolvedDays,
        poc_length_unit: form.lengthUnit,
        forecast_model: form.forecastModel,
      } as any
      const { error: cErr } = await supabase
        .from('clients')
        .update(clientUpdate)
        .eq('id', clientId as any)
      if (cErr) throw new Error(`clients update failed: ${cErr.message}`)

      // 2. Upsert client_goals rows. Skip metrics with all-empty targets.
      // Date-range derivation differs by mode:
      //   monthly  → start = first of month, end = last of month
      //   else     → start = form.startDate, end = start + resolvedDays
      let start: string | null = null
      let end: string | null = null
      if (form.lengthUnit === 'monthly') {
        const ref = form.startDate ? new Date(form.startDate) : new Date()
        start = firstOfMonthIso(ref)
        end = lastOfMonthIso(ref)
      } else if (form.startDate && resolvedDays) {
        start = form.startDate
        end = new Date(new Date(form.startDate).getTime() + resolvedDays * 86_400_000)
          .toISOString()
          .slice(0, 10)
      }

      const rowsToUpsert: Array<Partial<GoalRow> & { client_id: string }> = []
      for (const m of METRIC_ORDER) {
        const t = form.targets[m]
        if (!start || !end) continue

        if (form.lengthUnit === 'monthly') {
          // Monthly mode = 1 column, stored in the m3 slot. Mirror across
          // m1/m2/m3 to satisfy the monotonic CHECK constraint and so any
          // "% of goal" calc using m3 just works.
          if (t.m3 === '') continue
          const v = Number(t.m3)
          if (!Number.isFinite(v)) continue
          rowsToUpsert.push({
            client_id: clientId,
            metric_name: metricNameForDb(m),
            poc_start_date: start,
            poc_end_date: end,
            m1_target: v,
            m2_target: v,
            m3_target: v,
            is_primary: m === 'positives',
          })
        } else {
          const m1 = Number(t.m1)
          const m2 = Number(t.m2)
          const m3 = Number(t.m3)
          const hasAny = t.m1 !== '' || t.m2 !== '' || t.m3 !== ''
          if (!hasAny) continue
          rowsToUpsert.push({
            client_id: clientId,
            metric_name: metricNameForDb(m),
            poc_start_date: start,
            poc_end_date: end,
            m1_target: Number.isFinite(m1) ? m1 : 0,
            m2_target: Number.isFinite(m2) ? m2 : 0,
            m3_target: Number.isFinite(m3) ? m3 : 0,
            is_primary: m === 'positives',
          })
        }
      }

      if (rowsToUpsert.length > 0) {
        const { error: gErr } = await supabase
          .from('client_goals')
          .upsert(rowsToUpsert as any, { onConflict: 'client_id,metric_name' })
        if (gErr) throw new Error(`client_goals upsert failed: ${gErr.message}`)
      }
    },
    onSuccess: () => {
      // Invalidate everything that reads POC config.
      qc.invalidateQueries({ queryKey: ['poc-pacing'] })
      qc.invalidateQueries({ queryKey: ['poc-editor-existing'] })
      qc.invalidateQueries({ queryKey: ['page-settings-goals'] })
      onClose()
    },
  })

  if (!open) return null

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(17,24,39,0.4)',
          zIndex: 999,
        }}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="poc-editor-title"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(720px, calc(100vw - 32px))',
          maxHeight: 'calc(100vh - 48px)',
          overflowY: 'auto',
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 16,
          padding: 0,
          zIndex: 1000,
          boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
          fontFamily: 'inherit',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 22px',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          <div>
            <div id="poc-editor-title" style={{ fontSize: 16, fontWeight: 700, color: '#111' }}>
              Edit POC Pacing
            </div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
              {clientName ?? 'Client'} · per-metric M1/M2/M3 targets + POC window
            </div>
          </div>
          <button
            onClick={onClose}
            type="button"
            style={{
              background: 'transparent',
              border: 'none',
              padding: 6,
              cursor: 'pointer',
              color: '#6b7280',
              borderRadius: 6,
            }}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 22px' }}>
          {!clientId && (
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              Pick a specific client from the top filter to edit POC pacing.
            </div>
          )}

          {clientId && isLoading && (
            <div style={{ fontSize: 13, color: '#6b7280' }}>Loading…</div>
          )}

          {clientId && !isLoading && (
            <>
              {/* POC window */}
              <div style={{ marginBottom: 18 }}>
                <SectionLabel>Pacing window</SectionLabel>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 8 }}>
                  <Field
                    label={form.lengthUnit === 'monthly' ? 'Target month' : 'Start date'}
                  >
                    {form.lengthUnit === 'monthly' ? (
                      <MonthStepper
                        valueIso={form.startDate}
                        onChange={(iso) => setForm((f) => ({ ...f, startDate: iso }))}
                      />
                    ) : (
                      <input
                        type="date"
                        value={form.startDate}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, startDate: e.target.value }))
                        }
                        style={inputStyle}
                      />
                    )}
                  </Field>
                  <Field label="Pacing mode">
                    <select
                      value={form.lengthUnit}
                      onChange={(e) => {
                        const next = e.target.value as LengthUnit
                        setForm((f) => {
                          const today = new Date()
                          // Default a sensible start when switching modes:
                          //   monthly  → first of current month
                          //   relationship → leave blank
                          //   else     → today
                          const defaultStart =
                            next === 'monthly'
                              ? firstOfMonthIso(today)
                              : next === 'relationship'
                                ? f.startDate
                                : f.startDate || today.toISOString().slice(0, 10)
                          return { ...f, lengthUnit: next, startDate: defaultStart }
                        })
                      }}
                      style={inputStyle}
                    >
                      {LENGTH_UNIT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                {form.lengthUnit === 'custom' && (
                  <div style={{ marginTop: 10 }}>
                    <Field label="Custom days">
                      <input
                        type="number"
                        min={1}
                        value={form.customDays}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            customDays: e.target.value === '' ? '' : Number(e.target.value),
                          }))
                        }
                        placeholder="e.g. 60"
                        style={{ ...inputStyle, maxWidth: 160 }}
                      />
                    </Field>
                  </div>
                )}
                <div style={{ marginTop: 8, fontSize: 11, color: '#6b7280' }}>
                  {form.lengthUnit === 'relationship'
                    ? 'Open-ended — no fixed pacing window. The pacing card will be hidden for this client.'
                    : form.lengthUnit === 'monthly'
                      ? `Pacing for ${
                          form.startDate
                            ? formatMonth(new Date(form.startDate))
                            : formatMonth(new Date())
                        } only. Targets reset each month — pick a different month later to set its goals.`
                      : resolvedDays == null
                        ? 'Set a length to compute the end date.'
                        : `Resolves to ${resolvedDays} days. End date: ${
                            form.startDate
                              ? new Date(
                                  new Date(form.startDate).getTime() + resolvedDays * 86_400_000,
                                )
                                  .toISOString()
                                  .slice(0, 10)
                              : '—'
                          }`}
                </div>
              </div>

              {/* Forecast model */}
              <div style={{ marginBottom: 18 }}>
                <SectionLabel>Forecast model</SectionLabel>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, marginBottom: 8 }}>
                  How the Forecast card projects end-of-POC value.
                </div>
                <select
                  value={form.forecastModel}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, forecastModel: e.target.value as ForecastModel }))
                  }
                  style={{ ...inputStyle, maxWidth: 360 }}
                >
                  {FORECAST_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <div style={{ marginTop: 6, fontSize: 11, color: '#9ca3af' }}>
                  {FORECAST_OPTIONS.find((o) => o.value === form.forecastModel)?.hint}
                </div>
              </div>

              {/* Targets */}
              <div>
                <SectionLabel>Targets per metric</SectionLabel>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, marginBottom: 10 }}>
                  {form.lengthUnit === 'monthly'
                    ? `Cumulative target for ${
                        form.startDate
                          ? formatMonth(new Date(form.startDate))
                          : formatMonth(new Date())
                      }. Leave a metric blank to skip it (no goal line on that tab).`
                    : 'Cumulative target at the end of each milestone month. Leave a metric blank to skip it (no goal line on that tab).'}
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns:
                      targetColumns.length === 1
                        ? '1.4fr 1fr'
                        : `1.4fr ${'1fr '.repeat(targetColumns.length).trim()}`,
                    gap: 8,
                    alignItems: 'center',
                  }}
                >
                  <ColHead></ColHead>
                  {targetColumns.map((c) => (
                    <ColHead key={c.slot}>{c.label}</ColHead>
                  ))}
                  {METRIC_ORDER.map((m) => (
                    <React.Fragment key={m}>
                      <div style={{ fontSize: 13, color: '#111' }}>
                        {PACING_METRIC_LABELS[m].replace(/^★\s*/, '')}
                      </div>
                      {targetColumns.map((c) => (
                        <input
                          key={c.slot}
                          type="number"
                          value={form.targets[m][c.slot]}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              targets: {
                                ...f.targets,
                                [m]: { ...f.targets[m], [c.slot]: e.target.value },
                              },
                            }))
                          }
                          placeholder="—"
                          style={inputStyle}
                        />
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '14px 22px',
            borderTop: '1px solid #e5e7eb',
            background: '#fafafa',
            borderRadius: '0 0 16px 16px',
          }}
        >
          <div style={{ fontSize: 11, color: saveMutation.isError ? '#dc2626' : '#9ca3af' }}>
            {saveMutation.isError
              ? `Save failed: ${(saveMutation.error as Error)?.message ?? 'unknown'}`
              : ''}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={onClose}
              style={btnSecondaryStyle}
              disabled={saveMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => saveMutation.mutate()}
              style={btnPrimaryStyle}
              disabled={saveMutation.isPending || !clientId}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

// ─── small subcomponents ─────────────────────────────────────────────────

/**
 * MonthStepper — replaces the fiddly native <input type="month"> for the
 * Monthly target mode. Three affordances, picked from least to most click:
 *   1. ‹ / › arrows step the month back/forward by 1
 *   2. Big month label in the middle (click → opens hidden native picker
 *      so the keyboard / direct-jump flow still works)
 *   3. A row of 5 quick-pick chips: prev2, prev1, current, next1, next2 —
 *      click any to jump straight to it without opening anything
 */
function MonthStepper({
  valueIso,
  onChange,
}: {
  valueIso: string                    // YYYY-MM-01
  onChange: (iso: string) => void
}) {
  const hiddenInputRef = React.useRef<HTMLInputElement | null>(null)
  const baseDate = valueIso ? new Date(valueIso) : new Date()
  if (Number.isNaN(baseDate.getTime())) baseDate.setTime(Date.now())

  const stepMonths = (delta: number) => {
    const next = new Date(baseDate.getFullYear(), baseDate.getMonth() + delta, 1)
    onChange(firstOfMonthIso(next))
  }

  // Quick-pick chips: 2 prev / current / 2 next, around `baseDate`
  const chipOffsets = [-2, -1, 0, 1, 2]
  const today = new Date()
  const todayIso = firstOfMonthIso(today)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Hidden native input — focused programmatically when the user
          clicks the central month label, so keyboard navigation + direct
          jump (e.g. type "2027-03") still work for power users. */}
      <input
        ref={hiddenInputRef}
        type="month"
        value={valueIso ? valueIso.slice(0, 7) : ''}
        onChange={(e) => e.target.value && onChange(`${e.target.value}-01`)}
        style={{
          position: 'absolute', opacity: 0, pointerEvents: 'none',
          width: 1, height: 1, overflow: 'hidden',
        }}
        aria-hidden="true"
        tabIndex={-1}
      />

      {/* Stepper row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '32px 1fr 32px',
          alignItems: 'center',
          border: '1px solid #d1d5db',
          borderRadius: 6,
          background: '#fff',
          overflow: 'hidden',
        }}
      >
        <button
          type="button"
          onClick={() => stepMonths(-1)}
          aria-label="Previous month"
          style={stepBtnStyle}
        >
          ‹
        </button>
        <button
          type="button"
          onClick={() => hiddenInputRef.current?.showPicker?.()}
          title="Click to pick another month"
          style={{
            background: 'transparent', border: 'none',
            padding: '7px 8px', fontSize: 13, fontWeight: 600,
            color: '#111', cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          {formatMonth(baseDate)}
        </button>
        <button
          type="button"
          onClick={() => stepMonths(1)}
          aria-label="Next month"
          style={{ ...stepBtnStyle, borderRight: 'none', borderLeft: '1px solid #e5e7eb' }}
        >
          ›
        </button>
      </div>

      {/* Quick chips: 2 prev / current / 2 next around the picked month */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {chipOffsets.map((offset) => {
          const d = new Date(baseDate.getFullYear(), baseDate.getMonth() + offset, 1)
          const iso = firstOfMonthIso(d)
          const isSelected = iso === valueIso
          const isToday = iso === todayIso
          return (
            <button
              key={iso}
              type="button"
              onClick={() => onChange(iso)}
              style={{
                padding: '3px 9px', fontSize: 11, fontWeight: 500,
                borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
                border: `1px solid ${isSelected ? '#2563eb' : '#e5e7eb'}`,
                background: isSelected ? '#dbeafe' : isToday ? '#f1f5f9' : '#fff',
                color: isSelected ? '#1d4ed8' : '#374151',
                position: 'relative',
              }}
              title={isToday ? 'Current month' : undefined}
            >
              {formatMonth(d)}
              {isToday && !isSelected && (
                <span style={{ marginLeft: 4, color: '#9ca3af', fontSize: 10 }}>·</span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

const stepBtnStyle: React.CSSProperties = {
  background: '#fff', border: 'none', padding: '7px 4px',
  fontSize: 16, lineHeight: 1, color: '#374151',
  cursor: 'pointer', fontFamily: 'inherit',
  borderRight: '1px solid #e5e7eb',
  // The right arrow's left-divider is implicit via the next column
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: '#374151',
      }}
    >
      {children}
    </div>
  )
}

function ColHead({ children }: { children?: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      {children}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>{label}</span>
      {children}
    </label>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  fontSize: 13,
  borderRadius: 6,
  border: '1px solid #d1d5db',
  background: '#fff',
  color: '#111',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
}

const btnPrimaryStyle: React.CSSProperties = {
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 600,
  background: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const btnSecondaryStyle: React.CSSProperties = {
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 600,
  background: '#fff',
  color: '#374151',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

// Re-export the gear icon helper used in the chart header so consumers
// don't have to import lucide-react directly.
export { Settings as SettingsIcon }
